import { useContext, useEffect, useMemo, useState } from 'react';
import { db } from 'utils/firebase';
import { AuthContext } from 'Contexts/AuthContext';
import { RestaurantContext } from 'Contexts/RestaurantContext';
import useSupportApi from 'CustomHooks/Support/useSupportApi';
import {
  SupportConversationDocument,
  SupportMessageDocument,
  SupportTicketStatus,
} from 'types/shared/documents/support';
import { RestaurantTicketSummary } from 'types/shared/routes/v03/support';

const OPEN_TICKET_STATUSES: SupportTicketStatus[] = [
  'backlog',
  'open',
  'in_progress',
  'needs_review',
];

const newestFirst = (
  a: { createdAt: number; updatedAt?: number },
  b: { createdAt: number; updatedAt?: number },
) => (b.updatedAt ?? b.createdAt) - (a.updatedAt ?? a.createdAt);

/**
 * Realtime list of the restaurant's support conversations. Queried by
 * `restaurantId` only (single-field index, no composite needed) and sorted
 * client-side — support volumes per restaurant are small.
 */
export const useSupportConversations = (enabled: boolean) => {
  const { restaurantId } = useContext(RestaurantContext);
  const [conversations, setConversations] = useState<
    SupportConversationDocument[]
  >([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!enabled || !restaurantId) {
      setConversations([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsubscribe = db
      .collection('supportConversations')
      .where('restaurantId', '==', restaurantId)
      .onSnapshot(
        (snapshot) => {
          setConversations(
            snapshot.docs.map((doc) => ({
              ...(doc.data() as SupportConversationDocument),
              id: doc.id,
            })),
          );
          setLoading(false);
        },
        (error) => {
          // Drop stale data if the realtime subscription errors out.
          console.error('support conversations listener failed', error);
          setConversations([]);
          setLoading(false);
        },
      );
    return unsubscribe;
  }, [enabled, restaurantId]);

  const sorted = useMemo(
    () => [...conversations].sort(newestFirst),
    [conversations],
  );

  return { conversations: sorted, loading };
};

/**
 * The restaurant's tickets, plus the open subset. Ticket docs are internal
 * (Firestore rules allow only admins), so this fetches the customer-safe
 * projection from the API instead of Firestore. Fetched per mount (the
 * SupportPanel remounts on every open) and whenever `refreshKey` changes —
 * the panel derives it from the conversations' linked ticket ids so tickets
 * created mid-session appear without reopening.
 */
export const useSupportTickets = (enabled: boolean, refreshKey = '') => {
  const { listTickets } = useSupportApi();
  const [tickets, setTickets] = useState<RestaurantTicketSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!enabled) {
      setTickets([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    listTickets()
      .then((result) => {
        if (cancelled) return;
        setTickets(result);
        setLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        // Keep the previously loaded list — a transient refetch failure must
        // not blank the panel.
        console.error('support tickets fetch failed', error);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, listTickets, refreshKey]);

  const openTickets = useMemo(
    () =>
      [...tickets]
        .filter((ticket) => OPEN_TICKET_STATUSES.includes(ticket.status))
        .sort(newestFirst),
    [tickets],
  );

  return { tickets, openTickets, loading };
};

/**
 * Message types a restaurant user may read — everything except internal ones
 * (notes, assignment events). MUST stay in lockstep with the firestore.rules
 * `supportConversations/messages` read rule and the composite messages index
 * (restaurantId/draft/type/createdAt): the rule is data-dependent, so a query
 * that doesn't mirror it exactly is rejected for non-admin users ("rules are
 * not filters").
 */
export const RESTAURANT_VISIBLE_MESSAGE_TYPES = [
  'message',
  'multiple_choice',
  'function_call',
];

/**
 * Realtime view of a single conversation: the conversation document plus its
 * `messages` subcollection (query shape dictated by the security rule, see
 * RESTAURANT_VISIBLE_MESSAGE_TYPES).
 */
export const useSupportConversation = (conversationId: string | null) => {
  const { restaurantId } = useContext(RestaurantContext);
  const [conversation, setConversation] =
    useState<SupportConversationDocument | null>(null);
  const [messages, setMessages] = useState<SupportMessageDocument[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!conversationId || !restaurantId) {
      setConversation(null);
      setMessages([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    // Clear the previous conversation's data so a switch doesn't briefly show
    // stale messages while the new snapshot loads.
    setConversation(null);
    setMessages([]);
    const conversationRef = db
      .collection('supportConversations')
      .doc(conversationId);

    const unsubscribeConversation = conversationRef.onSnapshot(
      (doc) => {
        setConversation(
          doc.exists
            ? { ...(doc.data() as SupportConversationDocument), id: doc.id }
            : null,
        );
      },
      (error) => {
        console.error('support conversation listener failed', error);
        setConversation(null);
      },
    );

    const unsubscribeMessages = conversationRef
      .collection('messages')
      .where('restaurantId', '==', restaurantId)
      .where('draft', '==', false)
      .where('type', 'in', RESTAURANT_VISIBLE_MESSAGE_TYPES)
      .orderBy('createdAt', 'asc')
      .onSnapshot(
        (snapshot) => {
          setMessages(
            snapshot.docs.map((doc) => ({
              ...(doc.data() as SupportMessageDocument),
              id: doc.id,
            })),
          );
          setLoading(false);
        },
        (error) => {
          console.error('support messages listener failed', error);
          setLoading(false);
        },
      );

    return () => {
      unsubscribeConversation();
      unsubscribeMessages();
    };
  }, [conversationId, restaurantId]);

  return { conversation, messages, loading };
};

/**
 * "resolved" (handled, left open) and "blocked" (we wait on something internal)
 * are support-side states; to the restaurant both still read as open.
 */
export const isOpenForRestaurant = (
  conversation: SupportConversationDocument,
) =>
  conversation.status === 'open' ||
  conversation.status === 'resolved' ||
  conversation.status === 'blocked';

/**
 * A conversation is "unread" when its last message is from someone other than
 * the restaurant (Support / AI / system) and is newer than this user's server
 * read mark. `readAt[uid]` is written by POST /read — messageCount/readBy can't
 * serve here because they only count guest messages, never support replies.
 */
export const isConversationUnread = (
  conversation: SupportConversationDocument,
  currentUid?: string,
) => {
  // Read state is per-uid; while auth is still resolving we can't know it —
  // better no dot than a wrong flash across every conversation.
  if (!currentUid) return false;
  const last = conversation.lastMessage;
  if (!last) return false;
  // Own (restaurant) messages never count as unread. Prefer the persisted
  // `fromCustomer` flag; fall back to uid equality for pre-`fromCustomer` data,
  // matching ChatView's role logic.
  const selfIds = [currentUid, conversation.user?.id].filter(Boolean);
  const own = last.fromCustomer ?? selfIds.includes(last.uid);
  if (own) return false;
  const readAt = (currentUid && conversation.readAt?.[currentUid]) || 0;
  return last.createdAt > readAt;
};

/** Number of conversations with an unread Support/AI message (header badge). */
export const useSupportUnreadCount = () => {
  const { uid } = useContext(AuthContext);
  const { restaurantId } = useContext(RestaurantContext);
  const [conversations, setConversations] = useState<
    SupportConversationDocument[]
  >([]);

  useEffect(() => {
    if (!restaurantId) {
      setConversations([]);
      return;
    }
    const unsubscribe = db
      .collection('supportConversations')
      .where('restaurantId', '==', restaurantId)
      .onSnapshot(
        (snapshot) =>
          setConversations(
            snapshot.docs.map((doc) => ({
              ...(doc.data() as SupportConversationDocument),
              id: doc.id,
            })),
          ),
        (error) => {
          console.error('support unread listener failed', error);
          setConversations([]);
        },
      );
    return unsubscribe;
  }, [restaurantId]);

  return useMemo(
    () =>
      conversations.filter(
        // Only open conversations demand attention — without this, historical
        // closed conversations (readAt starts empty for everyone) would
        // inflate the badge after rollout.
        (c) =>
          isOpenForRestaurant(c) && isConversationUnread(c, uid || undefined),
      ).length,
    [conversations, uid],
  );
};
